import type mapboxgl from 'mapbox-gl';
import type { MapsIndoorsInstance, MapView } from './@types.js';
import { createQrCodeWidget } from './components/QrCode.js';
import {
  createPollingController,
  type PollingController,
} from './meetingRoomAvailability/polling.js';
import { createMeetingRoomAvailabilityFetcher } from './meetingRoomAvailability.js';
import {
  createKioskLocationHandler,
  setKioskIcon,
  setKioskMarkerAnimated,
} from './utils/kioskIcon.js';
import { parseNumber } from './utils/numbers.js';

interface AppConfig {
  venue: string | null;
  floor: string | null;
  startZoomLevel: string | null;
  mapboxAccessToken: string | null;
  apiKey: string | null;
  solutionId: string | null;
  bearing: string | null;
  pitch: string | null;
  latitude: string | null;
  longitude: string | null;
  kioskLocationId: string | null;
  meetingRoomAvailabilityApiKey: string | null;
  building: string | null;
  kioskAnimation: boolean;
}

type Venue = ReturnType<MapsIndoorsInstance['getVenue']> | undefined;

const createContainer = (rootElement: HTMLElement) => {
  rootElement.textContent = '';

  const container = document.createElement('div');
  container.style.display = 'block';
  container.style.height = '100vh';
  container.style.width = '100vw';
  container.style.position = 'relative';
  rootElement.append(container);

  return container;
};

const createMapContainer = (rootElement: HTMLElement) => {
  rootElement.textContent = '';

  const mapContainer = document.createElement('div');
  mapContainer.style.height = '100%';
  mapContainer.style.width = '100%';
  rootElement.append(mapContainer);

  return mapContainer;
};

const parseAppConfig = (search: string): AppConfig => {
  const params = new URLSearchParams(search);

  return {
    venue: params.get('venue'),
    floor: params.get('floor'),
    startZoomLevel: params.get('startZoomLevel'),
    mapboxAccessToken: params.get('mapboxAccessToken'),
    apiKey: params.get('apiKey'),
    solutionId: params.get('solutionId'),
    bearing: params.get('bearing'),
    pitch: params.get('pitch'),
    latitude: params.get('latitude'),
    longitude: params.get('longitude'),
    kioskLocationId: params.get('kioskLocationId'),
    meetingRoomAvailabilityApiKey: params.get('meetingRoomAvailabilityApiKey'),
    building: params.get('building'),
    kioskAnimation: params.get('kioskAnimation') === 'true',
  };
};

const configureInitialMapViewState = (
  mapView: MapView,
  kioskHandler: ReturnType<typeof createKioskLocationHandler>,
  kioskLocationId: string | null,
  meetingRoomPolling: PollingController,
) => {
  console.log('configureInitialMapViewState called with hide 3D features');

  const map = mapView.getMap();

  mapView.tilt(0, 2000);
  mapView.hideFeatures([
    mapView.FeatureType.MODEL3D,
    mapView.FeatureType.WALLS3D,
    mapView.FeatureType.EXTRUSION3D,
    mapView.FeatureType.EXTRUDEDBUILDINGS,
  ]);

  map.dragPan?.disable();
  map.scrollZoom?.disable();
  map.boxZoom?.disable();
  map.keyboard?.disable();
  map.doubleClickZoom?.disable();
  map.touchZoomRotate?.disable();

  void kioskHandler.handle(mapView, kioskLocationId);

  meetingRoomPolling.stop();

  const beginPolling = () => {
    meetingRoomPolling.start();
  };

  if (map.loaded()) {
    beginPolling();
  } else {
    map.once('idle', beginPolling);
  }
};

const createBuildingChangeController = (
  mapsIndoorsInstance: MapsIndoorsInstance,
  floor: string | null,
  kioskLocationId: string | null,
  meetingRoomPolling: PollingController,
  kioskHandler: ReturnType<typeof createKioskLocationHandler>,
) => {
  console.log('createBuildingChangeController called');
  let pendingMapLoadHandler: (() => void) | undefined;
  let mapboxMap: mapboxgl.Map | undefined;

  const configure = (mapView: MapView) => {
    pendingMapLoadHandler = undefined;
    mapsIndoorsInstance.setFloor(floor ?? null);
    configureInitialMapViewState(mapView, kioskHandler, kioskLocationId, meetingRoomPolling);
  };

  const handleBuildingChanged = async () => {
    const mapView = mapsIndoorsInstance.getMapView();
    if (!mapView) {
      return;
    }

    const map = mapView.getMap();

    if (pendingMapLoadHandler && mapboxMap) {
      console.log('Removing previous pending map load handler');
      mapboxMap.off('load', pendingMapLoadHandler);
      pendingMapLoadHandler = undefined;
    }

    mapboxMap = map;

    if (map.isStyleLoaded()) {
      console.log('Map style is loaded');
      configure(mapView);
    } else {
      console.log('Map style is not loaded yet');
      pendingMapLoadHandler = () => {
        configure(mapView);
      };
      map.once('load', pendingMapLoadHandler);
    }
  };

  return { handleBuildingChanged };
};

const resolveRoomsHarvesterBuildingName = (
  venue: Venue,
  fallbackBuilding: string | null,
): string | null => {
  const roomiFieldValue = venue?.venueInfo?.fields?.roomi_calendars_building?.value;
  const roomsHarvesterBuildingName = roomiFieldValue || fallbackBuilding;

  if (!roomsHarvesterBuildingName) {
    console.warn(
      'No building information available: set venueInfo.fields.roomi_calendars_building.value or provide a building query param',
    );
  }

  return roomsHarvesterBuildingName || null;
};

const centerMapToVenue = ({
  config,
  mapsIndoorsInstance,
  venue,
}: {
  config: Pick<AppConfig, 'latitude' | 'longitude'>;
  mapsIndoorsInstance: MapsIndoorsInstance;
  venue: Venue;
}): { lat: number; lng: number } | null => {
  if (config.latitude && config.longitude) {
    return null;
  }

  const coordinates = venue?.anchor?.coordinates;

  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }

  const [lng, lat] = coordinates;

  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return null;
  }

  const mapView = mapsIndoorsInstance.getMapView();
  const map = mapView?.getMap();
  map?.setCenter({ lat, lng });

  return { lat, lng };
};

const registerBuildingChangeHandler = ({
  instance,
  floor,
  kioskLocationId,
  meetingRoomPolling,
  kioskHandler,
}: {
  instance: MapsIndoorsInstance;
  floor: string | null;
  kioskLocationId: string | null;
  meetingRoomPolling: PollingController;
  kioskHandler: ReturnType<typeof createKioskLocationHandler>;
}) => {
  const { handleBuildingChanged } = createBuildingChangeController(
    instance,
    floor,
    kioskLocationId,
    meetingRoomPolling,
    kioskHandler,
  );

  void handleBuildingChanged();
  instance.on('building_changed', handleBuildingChanged);
};

export const createApp = (rootElement: HTMLElement) => {
  const config = parseAppConfig(window.location.search);
  const container = createContainer(rootElement);
  const qrCodeWidget = createQrCodeWidget(container);

  let mapsIndoorsInstance: MapsIndoorsInstance | null = null;
  let roomsHarvesterBuildingName: string | null = null;

  const kioskAnimationDelayMs = 10_000;
  let kioskAnimationReady = !config.kioskAnimation;
  let kioskAnimationPausedForFetch = false;

  const kioskHandler = createKioskLocationHandler(
    (locationId) => qrCodeWidget.setKioskLocationId(locationId),
    (mapView, location) =>
      setKioskIcon(mapView, location, {
        animate: config.kioskAnimation && kioskAnimationReady,
      }),
  );

  if (config.kioskAnimation) {
    setTimeout(() => {
      kioskAnimationReady = true;
      if (!kioskAnimationPausedForFetch) {
        setKioskMarkerAnimated(kioskHandler.getMarker?.(), true);
      }
    }, kioskAnimationDelayMs);
  }

  let meetingRoomFetcher: ReturnType<typeof createMeetingRoomAvailabilityFetcher> | null = null;

  const poolingHandler = async () => {
    // Disable kiosk animation during heavy map updates
    if (config.kioskAnimation) {
      kioskAnimationPausedForFetch = true;
      setKioskMarkerAnimated(kioskHandler.getMarker?.(), false);
    }

    await meetingRoomFetcher?.fetch();

    // Re-enable animation when the map becomes idle or after a short delay
    if (config.kioskAnimation) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 2000);
      });
      kioskAnimationPausedForFetch = false;
      if (kioskAnimationReady) {
        setKioskMarkerAnimated(kioskHandler.getMarker?.(), true);
      }
    }
  };

  const meetingRoomPolling = createPollingController(poolingHandler);

  if (!config.apiKey) {
    console.error('MapsIndoors apiKey query param is missing');
    return;
  }

  if (!config.mapboxAccessToken) {
    console.error('mapboxAccessToken query param is missing');
    return;
  }

  if (!window.mapboxgl) {
    console.error('Mapbox GL script is not loaded');
    return;
  }

  if (!window.mapsindoors?.mapView) {
    console.error('MapsIndoors script is not loaded');
    return;
  }

  const mapContainer = createMapContainer(container);

  // Center will be finalized after MapsIndoors is ready so we can
  // optionally fall back to venue.anchor.coordinates if URL params
  // are not provided.
  let initialCenterLat = parseNumber(config.latitude, 0);
  let initialCenterLng = parseNumber(config.longitude, 0);

  const mapViewOptions: Record<string, unknown> = {
    accessToken: config.mapboxAccessToken,
    element: mapContainer,
    zoom: parseNumber(config.startZoomLevel, 18.8),
    maxZoom: 22,
    bearing: parseNumber(config.bearing, 0),
    pitch: parseNumber(config.pitch, 0),
    showMapMarkers: true,
    center: {
      lat: initialCenterLat,
      lng: initialCenterLng,
    },
    pixelRatio: 1,
    // Perf-friendly knobs
    antialias: false, // no MSAA
    preserveDrawingBuffer: false,
    failIfMajorPerformanceCaveat: false,
    fadeDuration: 0, // kill crossfade animations
    crossSourceCollisions: false,
    interactive: false,
  };

  const mapViewInstance = new window.mapsindoors.mapView.MapboxV3View(mapViewOptions);

  const mapsIndoorsOptions: Record<string, unknown> = {
    apiKey: config.apiKey,
    mapView: mapViewInstance,
  };

  if (config.venue) {
    mapsIndoorsOptions.venue = config.venue;
  }

  const mapsIndoorsConstructor = window.mapsindoors?.MapsIndoors;
  mapsIndoorsConstructor.setMapsIndoorsApiKey(config.apiKey);

  if (config.solutionId) {
    mapsIndoorsConstructor.setSolutionId(config.solutionId);
  }

  const instance = new mapsIndoorsConstructor(mapsIndoorsOptions);

  const readyHandler = () => {
    mapsIndoorsInstance = instance;

    const venue = mapsIndoorsInstance.getVenue?.();

    roomsHarvesterBuildingName = resolveRoomsHarvesterBuildingName(venue, config.building);

    if (config.meetingRoomAvailabilityApiKey) {
      meetingRoomFetcher = createMeetingRoomAvailabilityFetcher({
        currentFloor: config.floor,
        mapsIndoorsInstance,
        meetingRoomAvailabilityApiKey: config.meetingRoomAvailabilityApiKey,
        building: roomsHarvesterBuildingName ?? config.building,
        venueId: config.venue,
      });
    }

    const anchorFallback = centerMapToVenue({
      config,
      mapsIndoorsInstance,
      venue,
    });

    if (anchorFallback) {
      initialCenterLat = anchorFallback.lat;
      initialCenterLng = anchorFallback.lng;
    }

    registerBuildingChangeHandler({
      instance,
      floor: config.floor,
      kioskLocationId: config.kioskLocationId,
      meetingRoomPolling,
      kioskHandler,
    });
  };

  instance.on('ready', readyHandler);
};
